export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';

class AuthenticationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AuthenticationError';
  }
}

class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

// Token refresh state - single promise pattern to prevent race conditions
let refreshPromise: Promise<boolean> | null = null;

const attemptTokenRefresh = async (): Promise<boolean> => {
  // If refresh is already in progress, return existing promise
  if (refreshPromise) {
    console.log('[API] Waiting for existing token refresh');
    return refreshPromise;
  }

  // Create a new refresh promise
  refreshPromise = (async () => {
    try {
      console.log('[API] Starting token refresh');
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.accessToken) {
          localStorage.setItem('accessToken', data.accessToken);
          // Dispatch event for authProvider to sync
          window.dispatchEvent(new CustomEvent('tokenRefreshed', {
            detail: { accessToken: data.accessToken }
          }));
        }
        console.log('[API] Token refresh successful');
        return true;
      }

      console.log('[API] Token refresh failed with status:', response.status);
      const errorText = await response.text();
      console.log('[API] Token refresh error response:', errorText);
      return false;
    } catch (error) {
      console.error('[API] Token refresh error:', error);
      return false;
    }
  })();

  const result = await refreshPromise;

  // Clear promise after short delay to prevent immediate re-refresh
  setTimeout(() => { refreshPromise = null; }, 1000);

  return result;
};

export const createHeaders = () => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  };

  // Add access token from localStorage for cross-origin requests
  // This is more reliable than cookies which can be blocked by browser ITP/privacy features
  const accessToken = localStorage.getItem('accessToken');
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  console.log('[API] Request headers:', headers);
  return headers;
};

const redirectToLogin = () => {
  console.log('[API] Redirecting to login page');
  const currentPath = window.location.pathname;
  const loginUrl = `/login?redirect=${encodeURIComponent(currentPath)}`;
  window.location.href = loginUrl;
};

export const handleResponse = async <T>(response: Response): Promise<T> => {
  const requestInfo = {
    url: response.url,
    status: response.status,
    statusText: response.statusText,
    headers: Object.fromEntries(response.headers.entries())
  };
  
  console.log('[API] Response info:', requestInfo);

  try {
    const responseText = await response.text();
    console.log('[API] Response text:', responseText);

    // Note: 401 handling is now done in ApiClient.request() with token refresh retry logic

    // Try to parse the response as JSON if it's not empty
    let parsedResponse: any = null;
    if (responseText) {
      try {
        parsedResponse = JSON.parse(responseText);
        console.log('[API] Parsed response:', parsedResponse);
      } catch (parseError) {
        console.warn('[API] Response is not JSON:', responseText);
      }
    }

    if (!response.ok) {
      let errorMessage: string;
      
      if (parsedResponse?.message || parsedResponse?.error) {
        errorMessage = parsedResponse.message || parsedResponse.error;
        // Handle nested error objects
        if (typeof errorMessage === 'object') {
          errorMessage = JSON.stringify(errorMessage);
        }
        // Include validation details if present
        if (parsedResponse.details) {
          console.error('[API] Validation details:', parsedResponse.details);
          errorMessage += '\nDetails: ' + JSON.stringify(parsedResponse.details);
        }
      } else if (responseText && responseText.length < 100) {
        errorMessage = responseText;
      } else {
        errorMessage = `Request failed with status ${response.status}`;
      }

      console.error('[API] Request failed:', {
        ...requestInfo,
        message: errorMessage
      });

      throw new ApiError(errorMessage, response.status);
    }

    // Handle successful response
    if (parsedResponse) {
      if ('data' in parsedResponse) {
        console.log('[API] Returning data property:', parsedResponse.data);
        return parsedResponse.data as T;
      }
      console.log('[API] Returning full response:', parsedResponse);
      return parsedResponse as T;
    }

    // Handle empty or non-JSON successful responses
    if (!responseText) {
      console.log('[API] Empty response, returning null');
      return null as T;
    }

    console.warn('[API] Non-JSON success response:', responseText);
    return responseText as T;
  } catch (error) {
    console.error('[API] Error in handleResponse:', error);
    throw error;
  }
};

export const isAuthenticated = async (): Promise<boolean> => {
  try {
    console.log('[API] Checking authentication status');
    const response = await fetch(`${API_BASE_URL}/api/v1/auth/check`, {
      credentials: 'include',
      headers: createHeaders()
    });
    
    const isAuth = response.ok;
    console.log('[API] Authentication check result:', isAuth);
    return isAuth;
  } catch (error) {
    console.error('[API] Auth check failed:', error);
    return false;
  }
};

export class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
    console.log('[API] Initialized ApiClient with base URL:', baseUrl);
  }

  private async request<T>(
    method: string,
    path: string,
    body?: any,
    params?: Record<string, string>,
    customHeaders?: Record<string, string>
  ): Promise<T> {
    let url = `${this.baseUrl}${path}`;

    // Append query parameters for GET requests
    if (method === 'GET' && params) {
      const query = new URLSearchParams(params).toString();
      if (query) {
        url += `?${query}`;
      }
    }

    console.log(`[API] ${method} request to ${url}`);

    const headers = {
      ...createHeaders(),
      ...customHeaders
    };

    const options: RequestInit = {
      method,
      headers,
      credentials: 'include', // Ensure cookies are sent with requests
      mode: 'cors'
    };

    if (body) {
      options.body = JSON.stringify(body);
      console.log('[API] Request body:', body);
    }

    try {
      console.log(`[API] Sending ${method} request:`, { url, options });
      let response = await fetch(url, options);

      // Handle 401 with token refresh
      if (response.status === 401) {
        console.log('[API] Received 401, attempting token refresh');

        const refreshSuccess = await attemptTokenRefresh();

        if (refreshSuccess) {
          // Retry the original request
          console.log('[API] Retrying original request after successful refresh');
          response = await fetch(url, options);
        } else {
          // Refresh failed, redirect to login
          console.log('[API] Token refresh failed, redirecting to login');
          redirectToLogin();
          throw new AuthenticationError('Session expired - please log in again');
        }
      }

      return handleResponse<T>(response);
    } catch (error) {
      console.error(`[API] ${method} request failed:`, error);
      throw error;
    }
  }

  async get<T>(path: string, params?: Record<string, string>, customHeaders?: Record<string, string>): Promise<T> {
    return this.request<T>('GET', path, undefined, params, customHeaders);
  }

  async post<T>(path: string, body: any, customHeaders?: Record<string, string>): Promise<T> {
    return this.request<T>('POST', path, body, customHeaders);
  }

  async put<T>(path: string, body: any, customHeaders?: Record<string, string>): Promise<T> {
    return this.request<T>('PUT', path, body, customHeaders);
  }

  async delete<T = void>(path: string, body?: any, customHeaders?: Record<string, string>): Promise<T> {
    return this.request<T>('DELETE', path, body, undefined, customHeaders);
  }

  async patch<T>(path: string, body: any, customHeaders?: Record<string, string>): Promise<T> {
    return this.request<T>('PATCH', path, body, undefined, customHeaders);
  }
}

export const api = new ApiClient();
